import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../../firebase/firestore.js";
import { auth } from "../../../firebase/auth.js";
import { writeAudit } from "../../../firebase/auditLogger.js";

/**
 * Business-wide security settings (shared by every user of this Firebase project).
 * The delete password is never stored — only a salted PBKDF2-SHA-256 hash.
 */
const SECURITY_DOC = () => doc(db, "settings", "security");
const ITERATIONS = 150000;
const MIN_LENGTH = 4;

const toHex = (buffer) => [...new Uint8Array(buffer)].map((b) => b.toString(16).padStart(2, "0")).join("");
const fromHex = (hex) => new Uint8Array(hex.match(/.{2}/g).map((h) => parseInt(h, 16)));

async function hashPassword(password, saltHex, iterations = ITERATIONS) {
  const key = await crypto.subtle.importKey("raw", new TextEncoder().encode(password), "PBKDF2", false, ["deriveBits"]);
  const bits = await crypto.subtle.deriveBits(
    { name: "PBKDF2", salt: fromHex(saltHex), iterations, hash: "SHA-256" },
    key,
    256
  );
  return toHex(bits);
}

function currentUser() {
  return auth.currentUser?.email || auth.currentUser?.uid || "admin";
}

function validateNew(password) {
  if (String(password || "").length < MIN_LENGTH) {
    throw new Error(`Delete password must be at least ${MIN_LENGTH} characters.`);
  }
}

export async function getSecuritySettings() {
  const snap = await getDoc(SECURITY_DOC());
  const data = snap.exists() ? snap.data() : {};
  return { deleteProtection: !!data.deleteProtection, ...data };
}

export async function verifyDeletePassword(password) {
  const settings = await getSecuritySettings();
  if (!settings.deleteProtection) return true;
  if (!password || !settings.salt || !settings.hash) return false;
  const hash = await hashPassword(password, settings.salt, settings.iterations || ITERATIONS);
  return hash === settings.hash;
}

async function savePassword(password, auditAction) {
  validateNew(password);
  const salt = toHex(crypto.getRandomValues(new Uint8Array(16)));
  const hash = await hashPassword(password, salt);
  await setDoc(
    SECURITY_DOC(),
    { deleteProtection: true, hash, salt, iterations: ITERATIONS, updatedAt: serverTimestamp(), updatedBy: currentUser() },
    { merge: true }
  );
  writeAudit(auditAction, { by: currentUser() });
}

export async function enableDeleteProtection(newPassword) {
  await savePassword(newPassword, "DELETE_PROTECTION_ENABLED");
}

export async function changeDeletePassword(currentPassword, newPassword) {
  if (!(await verifyDeletePassword(currentPassword))) throw new Error("Current delete password is wrong.");
  await savePassword(newPassword, "DELETE_PROTECTION_CHANGED");
}

/** Turn protection off. `currentPassword` may be null when the caller already re-verified the login password. */
export async function disableDeleteProtection(currentPassword, { reauthenticated = false } = {}) {
  if (!reauthenticated && !(await verifyDeletePassword(currentPassword))) throw new Error("Current delete password is wrong.");
  await setDoc(
    SECURITY_DOC(),
    { deleteProtection: false, hash: "", salt: "", updatedAt: serverTimestamp(), updatedBy: currentUser() },
    { merge: true }
  );
  writeAudit("DELETE_PROTECTION_DISABLED", { by: currentUser(), viaLoginPassword: reauthenticated });
}

/** "Forgot delete password": set a new one after the login password was re-verified. */
export async function resetDeletePassword(newPassword) {
  await savePassword(newPassword, "DELETE_PROTECTION_RESET");
}
